import { CompetencyNugget } from '@backrs_ui/grow-ui/src/components/Lubav/nuggets/CompetencyNugget';
import { ContactNugget } from '@backrs_ui/grow-ui/src/components/Lubav/nuggets/ContactNugget';
import { ResourceNugget } from '@backrs_ui/grow-ui/src/components/Lubav/nuggets/ResourceNugget';
import { ScholarshipNugget } from '@backrs_ui/grow-ui/src/components/Lubav/nuggets/ScholarshipNugget';
import { MainResponseData } from '@backrs_ui/grow-ui/src/models/models';

import { Box } from '@mui/material';

import { ActionPlanFormatter } from './output_formatters/ActionPlanFormatter';
import { CommonThreadsFormatter } from './output_formatters/CommonThreads';
import { JobPostingsFormatter } from './output_formatters/JobOpportunitiesFormatter';
import { LetterOfRecommendationFormatter } from './output_formatters/LetterOfRecommendation';
import { SchoolsFormatter } from './output_formatters/SchoolFormatter';
import { WhatAreYourChancesFormatter } from './output_formatters/WhatAreYourChances';
import { ToolNugget } from '../Lubav/nuggets/ToolNugget';

type OutputBoxProps = Readonly<{
  responseData?: MainResponseData;
  tab?: string;
  solutionSession: string;
  compactMode?: boolean;
}>;

const getSortedContacts = (resources?: MainResponseData['resources']) => {
  const contacts = [
    ...(resources?.backrs?.content || []),
    ...(resources?.students?.content || []),
  ].sort((a, b) => (b.metadata?.relevance_score || 0) - (a.metadata?.relevance_score || 0));

  return contacts;
};

const getSortedResources = (resourcesData?: MainResponseData['resources']) => {
  const competencies = resourcesData?.competencies?.content || [];
  const stories = resourcesData?.stories?.content || [];
  const learning = resourcesData?.learning?.content || [];
  const tools = resourcesData?.tools?.content || [];

  return [
    ...competencies.map((item) => ({ type: 'competency' as const, item })),
    ...stories.map((item) => ({ type: 'stories' as const, item })),
    ...learning.map((item) => ({ type: 'learning' as const, item })),
    ...tools.map((item) => ({ type: 'tool' as const, item })),
  ].sort(
    (a, b) => (b.item.metadata?.relevance_score || 0) - (a.item.metadata?.relevance_score || 0),
  );
};

export const OutputBox = ({
  responseData,
  tab = '',
  solutionSession,
  compactMode = false,
}: OutputBoxProps) => {
  switch (tab) {
    case 'main_content':
      if (!responseData || !responseData.main_response) {
        return <div></div>;
      } else {
        switch (solutionSession) {
          case 'college_shortlist':
            return (
              <SchoolsFormatter
                schools={{
                  safe: responseData.main_response.safe,
                  stretch: responseData.main_response.stretch,
                  target: responseData.main_response.target,
                }}
              />
            );
          case 'improve_your_test_scores':
            return (
              <ActionPlanFormatter
                actionPlan={responseData.main_response}
                compactMode={compactMode}
              />
            );
          case 'lower_cost_of_college':
            return (
              <Box sx={resourcesStyle}>
                {responseData.main_response.scholarships?.map((scholarship) => (
                  <ScholarshipNugget key={scholarship.id} scholarship={scholarship} />
                )) || []}
              </Box>
            );
          case 'jobs_you_might_love':
            return (
              <JobPostingsFormatter jobPostings={responseData.main_response.opportunities || []} />
            );
          case 'common_threads':
            if (responseData.main_response.metadata && responseData.main_response.id) {
              return (
                <CommonThreadsFormatter
                  contactWithCommonalities={{
                    metadata: responseData.main_response.metadata,
                    id: responseData.main_response.id,
                    relevance: responseData.main_response.relevance,
                    insights: responseData.main_response.insights || [],
                    questions: responseData.main_response.questions || [],
                    commonalities: responseData.main_response.commonalities || '',
                  }}
                />
              );
            }

            return <div></div>;
          case 'letters_of_rec':
            return (
              <LetterOfRecommendationFormatter letter={responseData.main_response.letter || ''} />
            );
          case 'what_are_your_chances':
            return (
              <WhatAreYourChancesFormatter
                actionPlan={responseData.main_response.action_plan || ''}
              />
            );
          default:
            return <div></div>;
        }
      }
    case 'contacts': {
      const contacts = getSortedContacts(responseData?.resources);

      return contacts.length > 0 ? (
        <Box sx={resourcesStyle}>
          {contacts.map((contact) => (
            <ContactNugget key={contact.id} contact={contact} type="profile-internal" />
          ))}
        </Box>
      ) : (
        <div></div>
      );
    }

    case 'resources': {
      const sortedItems = getSortedResources(responseData?.resources);

      return sortedItems.length > 0 ? (
        <Box sx={resourcesStyle}>
          {sortedItems.map(({ type, item }) => {
            switch (type) {
              case 'competency':
                return <CompetencyNugget key={item.id} competency={item} />;
              case 'tool':
                return <ToolNugget key={item.id} tool={item} />;
              default:
                return <ResourceNugget key={item.id} resource={item} />;
            }
          })}
        </Box>
      ) : (
        <div></div>
      );
    }

    default:
      return <div></div>;
  }
};

const resourcesStyle: React.CSSProperties = {
  gap: '16px',
  display: 'flex',
  flexDirection: 'column',
};
